import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

import styled, { useTheme } from "styled-components/macro";

import FnrLoadingBricks from "UI/FnrLoadingBricks2";

FnrAuthCodeInput.propTypes = {
  code: PropTypes.string,
  handleCodeChange: PropTypes.func,
  isLoading: PropTypes.bool,
  accentColor: PropTypes.string,
  loadingBricksColor: PropTypes.string,
  fontSize: PropTypes.number,
  style: PropTypes.object,
  size: PropTypes.string,
  staticBorderColor: PropTypes.bool,
  borderRadius: PropTypes.string,
};

export default function FnrAuthCodeInput({
  code,
  handleCodeChange,
  isLoading,
  accentColor,
  bgColor,
  loadingBricksColor,
  fontSize,
  style,
  size,
  staticBorderColor,
  borderRadius,
}) {
  const {
    colors: { fnBlue, fnDarkBlue },
  } = useTheme();
  const inputRef = useRef();

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  return (
    <Wrapper
      style={{
        "--accent-color": accentColor || fnBlue,
        "--bg-color": bgColor,
        ...style,
      }}
      $size={size}
      $staticBorderColor={staticBorderColor}
      $borderRadius={borderRadius}
    >
      <FnrLoadingBricks
        loading={isLoading}
        color={loadingBricksColor || accentColor || fnDarkBlue}
      >
        <BeforeResend>
          {code.length === 0 && (
            <Placeholder>
              <Circle $size={size} />
              <Circle $size={size} />
              <Circle $size={size} />
              <Circle $size={size} />
            </Placeholder>
          )}
          <Input
            data-cy="code"
            aria-label="auth-code-input"
            ref={inputRef}
            value={code}
            onChange={handleCodeChange}
            disabled={isLoading}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            style={{
              textIndent: code.length > 0 ? 12 : 0,
              fontSize: fontSize,
              lineHeight: fontSize + "px",
              borderRadius: "9999px"
            }}
            $size={size}
          />
        </BeforeResend>
      </FnrLoadingBricks>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 5px;
  border: 2px solid
    ${({ $staticBorderColor }) =>
      $staticBorderColor ? "var(--accent-color)" : "#bec3c7"};
  border-radius: ${({ $borderRadius }) => $borderRadius || "9px"};
  background: var(--bg-color);
  display: flex;
  align-items: center;
  overflow: hidden;
  max-width: ${({ $size }) => ($size === "small" ? "150" : "200")}px;
  max-height: ${({ $size }) => ($size === "small" ? "50px" : "initial")};
  margin: 0;
  transition: border 0.6s;
  position: relative;
  &:hover,
  &:focus-within {
    transition: border 0.2s;
    border: 2px solid var(--accent-color);
  }
`;

const Input = styled.input`
  box-sizing: border-box;
  text-align: center;
  width: 100%;
  font-size: ${({ $size }) => ($size === "small" ? "30" : "50")}px;
  line-height: ${({ $size }) => ($size === "small" ? "30" : "50")}px;
  padding: 8px;
  font-weight: 600;
  color: #474747;
  border-radius: 5px;
  background: none;
  border: none;
  font-variant-numeric: tabular-nums;
  outline: none;
  letter-spacing: 12px;
  text-indent: 12px;
  position: relative;
  z-index: 100;
`;

const Placeholder = styled.div`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 14px;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
const Circle = styled.div`
  background: #98a4a5;
  width: ${({ $size }) => ($size === "small" ? "12" : "18")}px;
  height: ${({ $size }) => ($size === "small" ? "12" : "18")}px;
  border-radius: 20px;
`;
const BeforeResend = styled.div`
  position: relative;
`;
